import {
  Alert,
  Box,
  Button,
  Card,
  Group,
  MultiSelect,
  Stack,
  Switch,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useOrgs } from '../api/orgs';
import { usePermissions } from '../api/permissions';
import { useCreateRole } from '../api/roles';

export function RoleCreatePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orgId = params.get('org') ?? '';
  const create = useCreateRole();
  const { data: orgs } = useOrgs();
  const permissions = usePermissions();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [permissionIds, setPermissionIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  if (!orgId) {
    return <Navigate to="/roles" replace />;
  }

  const orgName = (orgs ?? []).find((o) => o.id === orgId)?.name ?? orgId;

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }

    create.mutate(
      {
        organisation_id: orgId,
        name: name.trim(),
        description: description.trim() || undefined,
        is_default: isDefault,
        permission_ids: permissionIds,
      },
      {
        onSuccess: (role) => {
          notifications.show({ color: 'green', message: 'Role created.' });
          navigate(`/roles/${role.id}/edit`);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            const parsed = (() => {
              try {
                return JSON.parse(err.body) as { message?: string };
              } catch {
                return null;
              }
            })();
            setError(parsed?.message ?? 'Could not create role.');
          } else {
            setError('Could not create role.');
          }
        },
      },
    );
  }

  return (
    <Stack gap="lg">
      <Title order={2}>Create role</Title>
      <Card withBorder padding="lg" maw={680}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack gap="md">
            {error && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                {error}
              </Alert>
            )}
            <Box>
              <Title order={6}>Organisation</Title>
              <span>{orgName}</span>
            </Box>
            <TextInput
              label="Name"
              required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              disabled={create.isPending}
            />
            <Textarea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              disabled={create.isPending}
              minRows={2}
              autosize
            />
            <Switch
              label="Mark as the org's default role"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.currentTarget.checked)}
              disabled={create.isPending}
            />
            <MultiSelect
              label="Permissions"
              searchable
              clearable
              value={permissionIds}
              onChange={setPermissionIds}
              data={(permissions.data ?? []).map((p) => ({
                value: p.id,
                label: p.permission,
              }))}
              disabled={create.isPending}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" component={Link} to={`/roles?org=${orgId}`}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending}>
                Create
              </Button>
            </Group>
          </Stack>
        </Box>
      </Card>
    </Stack>
  );
}
